import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Users, CreditCard, Mail, Building2, Network, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCES = {
    '': { label: 'Tout', icon: ScrollText },
    reseau: { label: 'Réseau AV', icon: Network },
    crm: { label: 'CRM', icon: Users },
    paiement: { label: 'Paiements', icon: CreditCard },
    email: { label: 'Emails', icon: Mail },
    workspace: { label: 'Espaces', icon: Building2 },
};
const DOT = { reseau: 'bg-emerald-500', crm: 'bg-blue-500', paiement: 'bg-amber-500', email: 'bg-rose-500', workspace: 'bg-violet-500' };

export default function SystemLog({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [items, setItems] = useState(null);
    const [source, setSource] = useState('');
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (src = source) => {
        setLoading(true);
        try {
            const r = await api.get('/tech/system-log', { params: src ? { source: src } : {} });
            setItems(r.data.items);
        } catch { toast.error('Chargement du journal impossible'); }
        finally { setLoading(false); }
    }, [api, source]);

    useEffect(() => { load(''); /* eslint-disable-next-line */ }, []);

    return (
        <div className="space-y-5" data-testid="system-log">
            <div className="flex flex-wrap items-center gap-2">
                {Object.entries(SOURCES).map(([k, m]) => (
                    <button key={k} type="button"
                            onClick={() => { setSource(k); load(k); }}
                            data-testid={`syslog-filter-${k || 'all'}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${source === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-surface border-border hover:bg-surface-muted'}`}>
                        <m.icon size={12} /> {m.label}
                    </button>
                ))}
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => load()} data-testid="syslog-refresh">
                    <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
                </Button>
            </div>
            {!items ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 text-foreground/50" data-testid="syslog-empty">Aucun événement pour ce filtre.</div>
            ) : (
                <div className="rounded-xl border border-border bg-surface divide-y divide-border/60">
                    {items.map((e, i) => (
                        <div key={`${e.at}-${i}`} className="flex items-start gap-3 px-4 py-2.5" data-testid="syslog-row">
                            <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${DOT[e.source] || 'bg-stone-400'}`} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm">{e.label}</div>
                                {e.actor && <div className="text-[11px] text-foreground/50">{e.actor}</div>}
                            </div>
                            <div className="text-[11px] text-foreground/50 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString('fr-FR') : ''}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
